/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { motion } from 'framer-motion';
import {
    LineChart, ShieldCheck, Users, Building2, MapPin, Sparkles,
    MessagesSquare, BadgeCheck,
} from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };

const COCKPIT_BULLETS = [
    { icon: LineChart, label: 'Suivi vendeurs' },
    { icon: ShieldCheck, label: 'Priorités quotidiennes' },
    { icon: Users, label: 'Pilotage des équipes' },
    { icon: Building2, label: "Qualité de l'accompagnement" },
    { icon: Sparkles, label: 'Visibilité groupe et agence' },
];

const RESEAU_BULLETS = [
    { icon: BadgeCheck, label: 'Profils professionnels vérifiés' },
    { icon: Building2, label: 'Pages agences & groupes' },
    { icon: MessagesSquare, label: 'Avis issus de dossiers Homly' },
    { icon: ShieldCheck, label: 'Affiliation vérifiée' },
    { icon: MapPin, label: 'Visibilité locale' },
];

/**
 * ConvSolution — Iter162 : « Les deux moteurs Homly »
 * Deux colonnes côte à côte : Cockpit · Réseau.
 * Phrase clé : « Le cockpit produit la preuve du travail. Le réseau
 * transforme cette preuve en confiance. »
 */
export const ConvSolution = () => {
    return (
        <section className="relative py-20 md:py-32 border-t border-white/5" data-testid="solution-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl">
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[10px] font-medium tracking-widest text-[#A19DAB] uppercase"
                          style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        La proposition Homly
                    </span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.6 }}
                        className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.1]"
                        style={DISPLAY}
                        data-testid="solution-title"
                    >
                        Deux moteurs.{' '}
                        <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                            Un seul produit.
                        </span>
                    </motion.h2>
                    <p className="mt-5 text-base md:text-lg text-[#A19DAB] leading-relaxed">
                        Le cockpit produit la preuve du travail. Le réseau transforme
                        cette preuve en confiance.
                    </p>
                </div>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {/* Cockpit */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.5 }}
                        className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-[#A855F7]/5 to-transparent p-7 md:p-9"
                        data-testid="solution-cockpit-card"
                    >
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-10 h-10 rounded-xl bg-[#A855F7]/15 flex items-center justify-center">
                                <LineChart className="w-5 h-5 text-[#A855F7]" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white" style={DISPLAY}>
                                Cockpit de pilotage
                            </h3>
                        </div>
                        <p className="text-sm text-[#A19DAB] leading-relaxed mb-6">
                            Pour les groupes, agences et managers : structurer le travail
                            quotidien, sécuriser la qualité de l'accompagnement vendeur,
                            piloter l'équipe sans micro-management.
                        </p>
                        <ul className="space-y-2.5">
                            {COCKPIT_BULLETS.map(({ icon: Icon, label }) => (
                                <li key={label} className="flex items-center gap-3 text-sm text-white/85">
                                    <Icon className="w-4 h-4 text-[#A855F7] flex-shrink-0" />
                                    {label}
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Réseau */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.5, delay: 0.08 }}
                        className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-[#FF914D]/8 to-transparent p-7 md:p-9"
                        data-testid="solution-reseau-card"
                    >
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-10 h-10 rounded-xl bg-[#FF914D]/15 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-[#FF914D]" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white" style={DISPLAY}>
                                Réseau de réputation
                            </h3>
                        </div>
                        <p className="text-sm text-[#A19DAB] leading-relaxed mb-6">
                            Pour les agents, agences et groupes : transformer chaque
                            accompagnement vendeur en réputation publique, vérifiée et
                            durable. L'agence est aussi évaluée et son historique
                            collectif reste valorisé sur sa page publique.
                        </p>
                        <ul className="space-y-2.5">
                            {RESEAU_BULLETS.map(({ icon: Icon, label }) => (
                                <li key={label} className="flex items-center gap-3 text-sm text-white/85">
                                    <Icon className="w-4 h-4 text-[#FF914D] flex-shrink-0" />
                                    {label}
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
